import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import { useTeacherDetail } from 'core/hooks';
import { PATHS } from 'router/config';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { OverviewTab, PayoutsTab } from './tabs';

type TabId = 'overview' | 'payouts';

/**
 * `/teachers/:id/details` — read-only teacher hub. Payouts tab lists every
 * expense tagged `related_teacher` plus a by-month bar chart (TZ §4.3).
 */
export const TeacherDetail = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [tab, setTab] = useState<TabId>('overview');

  const { teacher, loading } = useTeacherDetail(id);

  if (loading && !teacher) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }
  if (!teacher) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.EmptyState>—</S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.BackLink onClick={() => history.push(PATHS.teachersUsers)}>
          {t('pages.teacherDetail.backToList')}
        </S.BackLink>

        <S.HeaderRow>
          <S.PageTitle>{teacher.fullName || '—'}</S.PageTitle>
        </S.HeaderRow>
        <S.SubTitle>
          {teacher.user?.email}
          {teacher.user?.phone ? ` · ${teacher.user.phone}` : ''}
        </S.SubTitle>

        <S.TabBar>
          <S.TabBarItem
            type="button"
            active={tab === 'overview'}
            onClick={() => setTab('overview')}
          >
            {t('pages.teacherDetail.tabs.overview')}
          </S.TabBarItem>
          <S.TabBarItem
            type="button"
            active={tab === 'payouts'}
            onClick={() => setTab('payouts')}
          >
            {t('pages.teacherDetail.tabs.payouts')}
          </S.TabBarItem>
        </S.TabBar>

        {tab === 'overview' && <OverviewTab teacher={teacher} />}
        {tab === 'payouts' && <PayoutsTab teacherId={teacher.id} />}
      </S.Wrapper>
    </MainTemplate>
  );
});
